import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import {
  MenuStructureService,
  NavItem,
  NavItemType,
  RouterLinkNavItemType,
} from '../menu-structure.service';

export const HOME_PATH = '/home';

@Injectable({
  providedIn: 'root',
})
export class BasicLayoutHelperService {
  private readonly router = inject(Router);
  private readonly menuStructureService = inject(MenuStructureService);

  currentPath = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
    ),
  );

  currentPathname = computed(() => this.currentPath()?.split(/[?#]/)[0]);

  isHomePage = computed(() => this.currentPathname() === HOME_PATH);

  public selectedNavItem = computed(() => {
    const currentPathWithQueryParams = this.currentPath();
    if (!currentPathWithQueryParams) {
      return null;
    }

    const currentPath = currentPathWithQueryParams.split('?')[0];

    const sidebarNavItems = this.menuStructureService.sidebarMenu.flatMap(
      (section) => section.items,
    );
    const allNavItems = [
      ...sidebarNavItems,
      ...this.menuStructureService.headerMenu,
    ];

    const currentNavItems = allNavItems
      .filter((item) => {
        if (item.type === NavItemType.RouterLink) {
          const declaredLink = item.routerLink;
          // check if link contains dynamic parts (e.q. :configurationId)
          if (declaredLink.includes(':')) {
            // replaces all dynamic parts (e.q. :configurationId) with placeholder which allows all characters except "/",
            const pattern = `^${declaredLink.replace(/:[^/]+/g, '[^/]+')}($|/)`;
            return new RegExp(pattern).test(currentPath);
          }

          return currentPath.startsWith(declaredLink);
        }
        return false;
      })
      // order by length of routerLink, since we want the most specific one first
      .sort((a, b) => {
        if (
          a.type !== NavItemType.RouterLink ||
          b.type !== NavItemType.RouterLink
        ) {
          return 0;
        }
        return b.routerLink.length - a.routerLink.length;
      });

    if (currentNavItems.length === 0) {
      return null;
    }

    return currentNavItems[0] as RouterLinkNavItemType;
  });

  isNavItemActive(navItem: NavItem): Signal<boolean> {
    return computed(() => {
      if (navItem.type === NavItemType.OnClick) {
        return false;
      }
      const selectedNavItem = this.selectedNavItem();

      if (!selectedNavItem && navItem.highlightSubpaths !== true) {
        return false;
      }
      const currentPath = this.currentPath();

      if (navItem.highlightSubpaths && currentPath && !selectedNavItem) {
        return navItem.routerLink.includes(currentPath);
      }

      if (!selectedNavItem) {
        return false;
      }

      return navItem.routerLink === selectedNavItem.routerLink;
    });
  }
}
